import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';

// Keep in sync with MAX_EMAIL_ATTACHMENTS_BYTES in tickets/sender_accounts.py.
const MAX_ATTACHMENTS_BYTES = 20 * 1024 * 1024;

const formatSize = (bytes) => (bytes < 1024 * 1024 ? `${Math.ceil(bytes / 1024)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`);

/**
 * Admin-only: review inbound emails that couldn't be tied to any ticket --
 * whether or not the sender is a customer we already recognise (see
 * tickets/email_ingest.py's _handle_unmatched_email). A known-customer
 * email doesn't necessarily mean they want a ticket opened -- could just
 * be a quick question -- so every unlinked email waits here for a human
 * to decide, rather than a ticket ever being auto-created.
 *
 * Each item can be: replied to directly (a plain email straight back to
 * the sender, no ticket involved at all -- for the "just answer the
 * question" case), converted into a real ticket (defaults to the known
 * customer if there is one, or picked from the dropdown, or a lightweight
 * ad-hoc contact is created from the sender's own From header), or
 * dismissed with no ticket created.
 */
const UnmatchedEmailQueueModal = ({ onClose }) => {
  const [emails, setEmails] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [boardChoice, setBoardChoice] = useState({});
  const [replyingId, setReplyingId] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [historyId, setHistoryId] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.getUnmatchedEmails(), api.getBoards()])
      .then(([items, brds]) => { setEmails(items); setBoards(brds); })
      .catch(() => toast.error('Could not load the unlinked email queue.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleConvert = async (item) => {
    try {
      setBusyId(item.id);
      const boardKey = boardChoice[item.id];
      await api.convertUnmatchedEmail(item.id, boardKey ? { board: boardKey } : {});
      toast.success('Converted into a new ticket.');
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not convert this email.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (item) => {
    if (!window.confirm('Dismiss this email without creating a ticket?')) return;
    try {
      setBusyId(item.id);
      await api.dismissUnmatchedEmail(item.id);
      load();
    } catch (error) {
      toast.error('Could not dismiss this email.');
    } finally {
      setBusyId(null);
    }
  };

  const startReply = (item) => {
    setReplyingId(item.id);
    setReplyDraft('');
    setReplyFiles([]);
  };

  const cancelReply = () => {
    setReplyingId(null);
    setReplyDraft('');
    setReplyFiles([]);
  };

  const handleReplyFilesChosen = (e) => {
    const chosen = Array.from(e.target.files || []);
    setReplyFiles((prev) => [...prev, ...chosen]);
    e.target.value = ''; // lets picking the exact same file again register as a change
  };

  const removeReplyFile = (index) => setReplyFiles((prev) => prev.filter((_, i) => i !== index));

  const replyAttachmentBytes = replyFiles.reduce((sum, f) => sum + f.size, 0);
  const replyAttachmentsTooLarge = replyAttachmentBytes > MAX_ATTACHMENTS_BYTES;

  const toggleHistory = async (item) => {
    if (historyId === item.id) { setHistoryId(null); setHistoryData(null); return; }
    setHistoryId(item.id);
    setHistoryData(null);
    setHistoryLoading(true);
    try {
      const data = await api.getUnmatchedEmailHistory(item.id);
      setHistoryData(data);
    } catch (error) {
      toast.error('Could not load conversation history.');
      setHistoryId(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSendReply = async (item) => {
    if (!replyDraft.trim()) return;
    if (replyAttachmentsTooLarge) {
      toast.error(`Attachments are too large (${formatSize(replyAttachmentBytes)}) -- keep them under ${formatSize(MAX_ATTACHMENTS_BYTES)} combined.`);
      return;
    }
    try {
      setBusyId(item.id);
      await api.replyToUnmatchedEmail(item.id, replyDraft.trim(), undefined, replyFiles);
      toast.success('Reply sent.');
      setReplyingId(null);
      setReplyDraft('');
      setReplyFiles([]);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not send that reply.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Unlinked emails</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <p style={{ fontSize: '0.8rem', color: '#aab7c4' }}>
          Emails that didn't match an existing ticket -- known customer or not. Reply directly if it's just a quick
          question that doesn't need a ticket, convert it into one (defaults to the known customer if there is one,
          otherwise an ad-hoc contact is created from the sender), or dismiss it -- e.g. it was spam.
        </p>

        {loading ? <p>Loading...</p> : emails.length === 0 ? (
          <p style={{ color: '#aab7c4' }}>Nothing waiting for review.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {emails.map((item) => (
              <div key={item.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <div>
                    <strong>{item.subject || '(no subject)'}</strong>
                    {item.matched_customer_name && (
                      <span className="label-chip" style={{ marginLeft: '0.4rem', background: 'var(--accent-soft-bg)', color: 'var(--accent)' }}>
                        Known customer: {item.matched_customer_name}
                      </span>
                    )}
                    <p style={{ fontSize: '0.8rem', color: '#aab7c4', margin: '0.2rem 0 0' }}>
                      From: {item.sender_name ? `${item.sender_name} <${item.sender_email || 'no address'}>` : (item.sender_email || 'unknown sender')}
                    </p>
                  </div>
                  <span style={{ fontSize: '0.72rem', color: '#aab7c4', whiteSpace: 'nowrap' }}>
                    {new Date(item.created_at).toLocaleString()}
                  </span>
                </div>
                <p
                  style={{
                    fontSize: '0.82rem', margin: '0.5rem 0', cursor: 'pointer',
                    whiteSpace: expandedId === item.id ? 'pre-wrap' : 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                  onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                  title="Click to expand/collapse"
                >
                  {item.body || '(empty message)'}
                </p>

                {item.attachments && item.attachments.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.5rem' }}>
                    {item.attachments.map((a) => (
                      <a
                        key={a.id} href={a.file} target="_blank" rel="noreferrer"
                        className="label-chip" style={{ textDecoration: 'none' }}
                      >
                        📎 {a.original_filename}
                      </a>
                    ))}
                  </div>
                )}

                {replyingId === item.id ? (
                  <div style={{ marginBottom: '0.5rem' }}>
                    <textarea
                      className="form-input" rows={4} style={{ width: '100%', resize: 'vertical' }}
                      placeholder={`Reply to ${item.sender_email || 'sender'}...`}
                      value={replyDraft}
                      onChange={(e) => setReplyDraft(e.target.value)}
                      autoFocus
                    />
                    {replyFiles.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', margin: '0.4rem 0' }}>
                        {replyFiles.map((f, i) => (
                          <span key={`${f.name}-${i}`} className="label-chip" style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            📎 {f.name} ({formatSize(f.size)})
                            <button
                              type="button" onClick={() => removeReplyFile(i)} aria-label={`Remove ${f.name}`}
                              style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, lineHeight: 1 }}
                            >
                              &times;
                            </button>
                          </span>
                        ))}
                        {replyAttachmentsTooLarge && (
                          <span style={{ fontSize: '0.72rem', color: '#ff9a9d' }}>
                            Over the {formatSize(MAX_ATTACHMENTS_BYTES)} limit -- remove something.
                          </span>
                        )}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem', alignItems: 'center' }}>
                      <button
                        type="button" className="btn-primary" disabled={busyId === item.id || !replyDraft.trim()}
                        onClick={() => handleSendReply(item)}
                      >
                        {busyId === item.id ? 'Sending...' : 'Send reply'}
                      </button>
                      <button type="button" className="btn-secondary" disabled={busyId === item.id} onClick={cancelReply}>Cancel</button>
                      <input
                        type="file" multiple id={`reply-attach-${item.id}`} style={{ display: 'none' }}
                        onChange={handleReplyFilesChosen}
                      />
                      <label
                        htmlFor={`reply-attach-${item.id}`} className="btn-secondary"
                        style={{ cursor: 'pointer', marginLeft: 'auto' }} title="Attach a file"
                      >
                        📎 Attach
                      </label>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" className="btn-secondary" disabled={busyId === item.id || !item.sender_email} onClick={() => startReply(item)}>
                      Reply directly
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => toggleHistory(item)}>
                      {historyId === item.id ? 'Hide history' : 'View history'}
                    </button>
                    <select
                      className="form-select" style={{ maxWidth: 200 }}
                      value={boardChoice[item.id] || ''}
                      onChange={(e) => setBoardChoice((p) => ({ ...p, [item.id]: e.target.value }))}
                    >
                      <option value="">Default board (Service Desk)</option>
                      {boards.map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
                    </select>
                    <button type="button" className="btn-primary" disabled={busyId === item.id} onClick={() => handleConvert(item)}>
                      {busyId === item.id ? 'Working...' : 'Convert to ticket'}
                    </button>
                    <button type="button" className="btn-secondary" disabled={busyId === item.id} onClick={() => handleDismiss(item)}>Dismiss</button>
                  </div>
                )}

                {historyId === item.id && (
                  <div style={{ marginTop: '0.6rem', borderTop: '1px solid var(--border-subtle)', paddingTop: '0.6rem' }}>
                    {historyLoading ? (
                      <p style={{ fontSize: '0.8rem', color: '#aab7c4' }}>Loading history...</p>
                    ) : (!historyData || historyData.events.length === 0) ? (
                      <p style={{ fontSize: '0.8rem', color: '#aab7c4' }}>No earlier conversation with this sender.</p>
                    ) : (
                      <div>
                        <p style={{ fontSize: '0.75rem', color: '#aab7c4', margin: '0 0 0.4rem' }}>
                          Conversation with {historyData.sender_name || historyData.sender_email}
                          {historyData.matched_customer ? ` (${historyData.matched_customer})` : ''} &mdash; {historyData.events.length} messages
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: 320, overflowY: 'auto' }}>
                          {historyData.events.map((ev, i) => (
                            <div key={i} style={{ borderLeft: `3px solid ${ev.type === 'reply' ? 'var(--accent)' : '#5a6b7b'}`, background: 'rgba(255,255,255,0.03)', padding: '0.4rem 0.6rem', borderRadius: '4px' }}>
                              <div style={{ fontSize: '0.72rem', color: '#aab7c4', display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                                <span>{ev.type === 'reply' ? `\u21A9 Reply${ev.author ? ` by ${ev.author}` : ''}` : `\u{1F4E5} From ${ev.from_name || ev.from_email || 'sender'}`}</span>
                                <span>{new Date(ev.at).toLocaleString()}</span>
                              </div>
                              {ev.subject && <div style={{ fontSize: '0.78rem', fontWeight: 600, margin: '0.15rem 0' }}>{ev.subject}</div>}
                              <div style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{ev.body || '(empty)'}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UnmatchedEmailQueueModal;
